import { useEffect, useState, useRef } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { uploadFile } from 'zite-file-upload-sdk';
import {
  getAdminModules, saveModule, deleteModule, getAdminQuestions, updateUser, getUsers,
  generateQuizQuestions, saveQuestion, getKnowledgeBase,
  getAdminEmployees,
  GetAdminModulesOutputType, GetUsersOutputType, GetAdminQuestionsOutputType,
  GenerateQuizQuestionsOutputType, GetKnowledgeBaseOutputType, GetAdminEmployeesOutputType,
} from 'zite-endpoints-sdk';
import EmployeeTable from '../components/admin/EmployeeTable';
import EmployeeDrawer from '../components/admin/EmployeeDrawer';
import OnboardingQueueTab from '../components/admin/OnboardingQueueTab';
import ComplianceAdminTab from '../components/admin/ComplianceAdminTab';
import AttensiAdminTab from '../components/admin/AttensiAdminTab';
import TrainingRequestsAdminTab from '../components/admin/TrainingRequestsAdminTab';
import EditRequestsAdminTab from '../components/admin/EditRequestsAdminTab';
import ReportsTab from '../components/admin/ReportsTab';
import MessagingTab from '../components/admin/MessagingTab';
import HandbookAdminTab from '../components/admin/HandbookAdminTab';
import RewardsAdminTab from '../components/admin/RewardsAdminTab';
import PoliciesAdminTab from '../components/admin/PoliciesAdminTab';
import UniformAdminTab from '../components/admin/UniformAdminTab';
import KeyManagementTab from '../components/admin/KeyManagementTab';
import VenueAdminTab from '../components/admin/VenueAdminTab';
import MaintenanceAdminTab from '../components/admin/MaintenanceAdminTab';
import ToolsAdminTab from '../components/admin/ToolsAdminTab';
import AdminDashboardTab from '../components/admin/AdminDashboardTab';
import AnnouncementsAdminTab from '../components/admin/AnnouncementsAdminTab';
import EventsAdminTab from '../components/admin/EventsAdminTab';
import CustomerContactsTab from '../components/admin/CustomerContactsTab';
import LabourOverviewTab from '../components/admin/LabourOverviewTab';
import RetryQueueTab from '../components/admin/RetryQueueTab';
import XeroPayrollTab from '../components/admin/XeroPayrollTab';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload, Loader2, Sparkles, Download, Menu } from 'lucide-react';
import QuestionManager from '../components/admin/QuestionManager';
import CategoryManager from '../components/admin/CategoryManager';
import ContentUploader from '../components/admin/ContentUploader';
import KnowledgeBaseManager from '../components/admin/KnowledgeBaseManager';
import { MarkdownEditor } from '@/components/markdown-editor';

type Mod = GetAdminModulesOutputType['modules'][0];
type User = GetUsersOutputType['users'][0];
type QList = GetAdminQuestionsOutputType['questions'];
type AIQuestion = GenerateQuizQuestionsOutputType['questions'][0];
type KBEntry = GetKnowledgeBaseOutputType['entries'][0];
type Employee = GetAdminEmployeesOutputType['employees'][0];

type NavGroup = { label: string; items: { id: string; label: string }[] };

const NAV_GROUPS: NavGroup[] = [
  { label: 'Overview', items: [{ id: 'dashboard', label: 'Dashboard' }, { id: 'announcements', label: 'Announcements' }] },
  { label: 'People', items: [{ id: 'employees', label: 'Employees' }, { id: 'onboarding', label: 'Onboarding' }, { id: 'contacts', label: 'Contacts' }, { id: 'staff', label: 'Staff Roles' }] },
  { label: 'Training & Compliance', items: [{ id: 'modules', label: 'Modules' }, { id: 'categories', label: 'Categories' }, { id: 'compliance', label: 'Compliance' }, { id: 'handbook', label: 'Handbook' }, { id: 'policies', label: 'Policies' }, { id: 'attensi', label: 'Attensi' }, { id: 'knowledge', label: 'Knowledge Bank' }, { id: 'upload', label: 'Upload Content' }] },
  { label: 'Operations', items: [{ id: 'venue', label: 'Venue & Rooms' }, { id: 'maintenance', label: 'Maintenance' }, { id: 'uniforms', label: 'Uniforms' }, { id: 'keys', label: 'Keys' }, { id: 'tools', label: 'Tools' }, { id: 'labour', label: 'Labour' }] },
  { label: 'Comms & Reports', items: [{ id: 'messaging', label: 'Messaging' }, { id: 'training-reqs', label: 'Training Requests' }, { id: 'edit-reqs', label: 'Edit Requests' }, { id: 'rewards', label: 'Rewards' }, { id: 'reports', label: 'Reports' }, { id: 'events', label: 'Events' }, { id: 'retry-queue', label: 'Retry Queue' }] },
  { label: 'Integrations', items: [{ id: 'xero-payroll', label: 'Xero Payroll' }] },
];

const blankForm = {
  title: '', categoryId: '', description: '', estimatedDuration: '',
  passMarkPercent: 80, displayOrder: 0, status: 'Draft' as 'Draft' | 'Published',
  approverId: '', contentType: '', department: '', contentBody: '',
};

export default function AdminPanel() {
  const { user } = useAuth();
  const [modules, setModules] = useState<Mod[]>([]);
  const [categories, setCategories] = useState<GetAdminModulesOutputType['categories']>([]);
  const [managers, setManagers] = useState<GetAdminModulesOutputType['managers']>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [kbEntries, setKbEntries] = useState<KBEntry[]>([]);
  const [loadingKb, setLoadingKb] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [employeeDrawerOpen, setEmployeeDrawerOpen] = useState(false);
  const [editMod, setEditMod] = useState<Mod | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [form, setForm] = useState({ ...blankForm });
  const [questions, setQuestions] = useState<QList>([]);
  const [loadingQ, setLoadingQ] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingContent, setUploadingContent] = useState(false);
  const [contentFiles, setContentFiles] = useState<{ url: string }[]>([]);
  const [sopFiles, setSopFiles] = useState<{ url: string }[]>([]);
  const [aiQuestions, setAiQuestions] = useState<AIQuestion[]>([]);
  const [generating, setGenerating] = useState(false);
  const [savingAI, setSavingAI] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const contentRef = useRef<HTMLInputElement>(null);

  const loadModules = () => {
    getAdminModules({}).then(d => { setModules(d.modules); setCategories(d.categories); setManagers(d.managers); }).catch(() => {});
  };

  const loadKb = () => {
    setLoadingKb(true);
    getKnowledgeBase({}).then(d => setKbEntries(d.entries)).catch(() => {}).finally(() => setLoadingKb(false));
  };

  useEffect(() => {
    if (!user) return;
    Promise.all([
      getAdminModules({}).then(d => { setModules(d.modules); setCategories(d.categories); setManagers(d.managers); }),
      getUsers({}).then(d => setUsers(d.users)),
    ]).finally(() => setLoading(false));
  }, [user]);

  const loadEmployees = () => {
    setLoadingEmployees(true);
    getAdminEmployees({}).then(d => setEmployees(d.employees)).catch(() => {}).finally(() => setLoadingEmployees(false));
  };

  useEffect(() => {
    if (activeTab === 'knowledge' && kbEntries.length === 0) loadKb();
    if (activeTab === 'employees' && employees.length === 0) loadEmployees();
  }, [activeTab]);

  const openNew = () => { setEditMod(null); setForm({ ...blankForm }); setContentFiles([]); setSopFiles([]); setQuestions([]); setAiQuestions([]); setSheetOpen(true); };
  const openEdit = (m: Mod) => {
    setEditMod(m);
    setForm({ title: m.title, categoryId: m.categoryId, description: m.description || '', estimatedDuration: m.estimatedDuration || '', passMarkPercent: m.passMarkPercent, displayOrder: m.displayOrder, status: m.status as 'Draft' | 'Published', approverId: m.approverId, contentType: m.contentType || '', department: m.department || '', contentBody: m.contentBody || '' });
    setContentFiles(m.contentFiles);
    setSopFiles(m.sopCard);
    setAiQuestions([]);
    setSheetOpen(true);
    setLoadingQ(true);
    getAdminQuestions({ moduleId: m.id }).then(d => setQuestions(d.questions)).finally(() => setLoadingQ(false));
  };

  const refreshQ = () => { if (editMod) getAdminQuestions({ moduleId: editMod.id }).then(d => setQuestions(d.questions)); };

  const generateAI = async () => {
    if (!editMod || !form.contentBody.trim()) { toast.error('Add training content first'); return; }
    setGenerating(true);
    setAiQuestions([]);
    try {
      const res = await generateQuizQuestions({ moduleId: editMod.id, contentBody: form.contentBody });
      setAiQuestions(res.questions);
    } catch (e: any) { toast.error(e.message || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const saveAIQuestions = async () => {
    if (!editMod || aiQuestions.length === 0) return;
    setSavingAI(true);
    try {
      for (const q of aiQuestions) {
        await saveQuestion({ moduleId: editMod.id, question: q.question, optionA: q.optionA, optionB: q.optionB, optionC: q.optionC, optionD: q.optionD, correctAnswer: q.correctAnswer, questionType: 'Standard' });
      }
      toast.success(`Saved ${aiQuestions.length} questions`);
      setAiQuestions([]);
      refreshQ();
    } catch { toast.error('Failed to save questions'); }
    finally { setSavingAI(false); }
  };

  const uploadContent = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingContent(true);
    try { const { fileUrl } = await uploadFile({ data: file, filename: file.name }); setContentFiles(f => [...f, { url: fileUrl }]); toast.success('File uploaded'); }
    catch { toast.error('Upload failed'); } finally { setUploadingContent(false); e.target.value = ''; }
  };

  const saveModuleForm = async () => {
    if (!form.title.trim() || !form.categoryId) { toast.error('Title and category are required'); return; }
    setSaving(true);
    try {
      const res = await saveModule({ id: editMod?.id, ...form, contentFiles, sopCard: sopFiles });
      toast.success(editMod ? 'Module updated' : 'Module created');
      if (!editMod) setEditMod({ ...form, id: res.id, categoryName: categories.find(c => c.id === form.categoryId)?.name || '', questionCount: 0, approverName: '', contentFiles, sopCard: sopFiles });
      loadModules();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const downloadSOP = (m: Mod) => {
    const content = `${m.title}\n${'='.repeat(m.title.length)}\n\n${m.contentBody || 'No content body'}\n`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${m.title.replace(/[^a-z0-9]/gi, '_')}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  const removeMod = async (id: string) => {
    try { await deleteModule({ id }); toast.success('Module deleted'); loadModules(); }
    catch { toast.error('Failed to delete'); }
  };

  const updateUserRole = async (userId: string, role: string, dept?: string) => {
    try {
      await updateUser({ id: userId, role, department: dept });
      setUsers(u => u.map(usr => usr.id === userId ? { ...usr, role, department: dept || usr.department } : usr));
      toast.success('Updated');
    } catch { toast.error('Failed to update'); }
  };

  const catList = categories.map(c => ({ id: c.id, name: c.name, description: (c as any).description || '', displayOrder: (c as any).displayOrder || 0, requiredForAllStaff: c.requiredForAllStaff || false }));
  const activeLabel = NAV_GROUPS.flatMap(g => g.items).find(i => i.id === activeTab)?.label || activeTab;

  if (loading) return <div className="p-8 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  const Sidebar = () => (
    <div className="space-y-1">
      {NAV_GROUPS.map(group => (
        <div key={group.label} className="mb-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-1 mt-2">{group.label}</p>
          {group.items.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors ${activeTab === item.id ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card px-4 md:px-6 py-3 flex items-center gap-3">
        <button className="md:hidden text-muted-foreground" onClick={() => setSidebarOpen(!sidebarOpen)}>
          <Menu className="w-5 h-5" />
        </button>
        <h1 className="text-base font-bold text-foreground">Admin Panel</h1>
        <span className="text-muted-foreground text-sm">/</span>
        <span className="text-sm text-muted-foreground">{activeLabel}</span>
      </div>

      <EmployeeDrawer employee={selectedEmployee} open={employeeDrawerOpen} onClose={() => setEmployeeDrawerOpen(false)} onRefresh={loadEmployees} />

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-56 bg-card border-r border-border p-3 overflow-y-auto z-50">
            <Sidebar />
          </div>
        </div>
      )}

      <div className="flex">
        <aside className="hidden md:block w-52 shrink-0 border-r border-border min-h-[calc(100vh-57px)] p-3 bg-card overflow-y-auto">
          <Sidebar />
        </aside>

        <main className="flex-1 p-4 md:p-6 min-w-0">
          {activeTab === 'dashboard' && <AdminDashboardTab onNavigate={setActiveTab} />}
          {activeTab === 'announcements' && <AnnouncementsAdminTab />}
          {activeTab === 'events' && <EventsAdminTab />}
          {activeTab === 'retry-queue' && <RetryQueueTab />}
          {activeTab === 'xero-payroll' && <XeroPayrollTab />}
          {activeTab === 'contacts' && <CustomerContactsTab />}
          {activeTab === 'labour' && <LabourOverviewTab />}
          {activeTab === 'employees' && <EmployeeTable employees={employees} loading={loadingEmployees} onSelectEmployee={emp => { setSelectedEmployee(emp); setEmployeeDrawerOpen(true); }} />}
          {activeTab === 'onboarding' && <OnboardingQueueTab />}
          {activeTab === 'compliance' && <ComplianceAdminTab />}
          {activeTab === 'attensi' && <AttensiAdminTab />}
          {activeTab === 'training-reqs' && <TrainingRequestsAdminTab />}
          {activeTab === 'edit-reqs' && <EditRequestsAdminTab />}
          {activeTab === 'reports' && <ReportsTab />}
          {activeTab === 'messaging' && <MessagingTab />}
          {activeTab === 'handbook' && <HandbookAdminTab />}
          {activeTab === 'policies' && <PoliciesAdminTab />}
          {activeTab === 'uniforms' && <UniformAdminTab />}
          {activeTab === 'keys' && <KeyManagementTab />}
          {activeTab === 'rewards' && <div><div className="mb-4"><h2 className="text-base font-semibold text-foreground">Rewards & Incentives</h2></div><RewardsAdminTab /></div>}
          {activeTab === 'venue' && <VenueAdminTab />}
          {activeTab === 'maintenance' && <div><div className="mb-4"><h2 className="text-base font-semibold text-foreground">Maintenance Log</h2></div><MaintenanceAdminTab /></div>}
          {activeTab === 'tools' && <div><div className="mb-4"><h2 className="text-base font-semibold text-foreground">Tools & Shift Control</h2></div><ToolsAdminTab /></div>}
          {activeTab === 'categories' && <CategoryManager categories={catList} onRefresh={loadModules} />}
          {activeTab === 'upload' && (
            <div className="max-w-2xl">
              <div className="mb-6"><h2 className="text-base font-semibold text-foreground mb-1">Upload & Process Content</h2></div>
              <ContentUploader categories={categories.map(c => ({ id: c.id, name: c.name }))} onSuccess={() => { loadModules(); }} />
            </div>
          )}
          {activeTab === 'knowledge' && (
            <div>
              <div className="mb-6"><h2 className="text-base font-semibold text-foreground mb-1">Knowledge Bank</h2></div>
              <KnowledgeBaseManager entries={kbEntries} loading={loadingKb} onRefresh={loadKb} />
            </div>
          )}
          {activeTab === 'staff' && (
            <div className="border border-border overflow-hidden rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Name / Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Department</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map(u => (
                    <tr key={u.id}>
                      <td className="px-4 py-3"><p className="font-medium text-foreground">{u.firstName} {u.lastName}</p><p className="text-xs text-muted-foreground">{u.email}</p></td>
                      <td className="px-4 py-3">
                        <Select value={u.role} onValueChange={v => updateUserRole(u.id, v, u.department)}>
                          <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="Trainee">Trainee</SelectItem><SelectItem value="Manager">Manager</SelectItem><SelectItem value="Admin">Admin</SelectItem></SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={u.department || 'none'} onValueChange={v => updateUserRole(u.id, u.role, v === 'none' ? '' : v)}>
                          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Set dept." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No department</SelectItem>
                            <SelectItem value="Front of House">Front of House</SelectItem>
                            <SelectItem value="Back of House">Back of House</SelectItem>
                            <SelectItem value="Kitchen">Kitchen</SelectItem>
                            <SelectItem value="Management">Management</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {activeTab === 'modules' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-muted-foreground">{modules.filter(m => m.status === 'Published').length} published · {modules.filter(m => m.status === 'Draft').length} drafts</p>
                <Button onClick={openNew} className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1" /> Add module</Button>
              </div>
              <div className="border border-border overflow-hidden rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Module</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden sm:table-cell">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Qs</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Status</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {modules.map(m => (
                      <tr key={m.id}>
                        <td className="px-4 py-3 font-medium text-foreground">{m.title}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground hidden sm:table-cell">{m.categoryName}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{m.questionCount}</td>
                        <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm ${m.status === 'Published' ? 'bg-primary text-primary-foreground' : 'border border-border text-muted-foreground'}`}>{m.status}</span></td>
                        <td className="px-4 py-3 flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(m)}><Pencil className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => downloadSOP(m)}><Download className="w-3 h-3" /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3 h-3" /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Delete "{m.title}"?</AlertDialogTitle></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => removeMod(m.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader className="mb-6"><SheetTitle>{editMod ? `Edit: ${editMod.title}` : 'New Module'}</SheetTitle></SheetHeader>
          <div className="space-y-4 mb-8">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Content Type</label>
                <Select value={form.contentType || 'none'} onValueChange={v => setForm(f => ({ ...f, contentType: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Not set</SelectItem><SelectItem value="Induction Section">Induction Section</SelectItem><SelectItem value="Training Module">Training Module</SelectItem><SelectItem value="SOP">SOP</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Department</label>
                <Select value={form.department || 'none'} onValueChange={v => setForm(f => ({ ...f, department: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select dept." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Not set</SelectItem><SelectItem value="All">All Departments</SelectItem><SelectItem value="Front of House">Front of House</SelectItem><SelectItem value="Back of House">Back of House</SelectItem><SelectItem value="Kitchen">Kitchen</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Title *</label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Module title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Category *</label>
                <Select value={form.categoryId} onValueChange={v => setForm(f => ({ ...f, categoryId: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Assigned Approver</label>
                <Select value={form.approverId || 'none'} onValueChange={v => setForm(f => ({ ...f, approverId: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unassigned</SelectItem>{managers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Description</label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="min-h-16" /></div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Content Body</label>
              <MarkdownEditor content={form.contentBody} onChange={v => setForm(f => ({ ...f, contentBody: v }))} height="dynamic-md" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Duration</label><Input value={form.estimatedDuration} onChange={e => setForm(f => ({ ...f, estimatedDuration: e.target.value }))} placeholder="e.g. 30 min" /></div>
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Pass mark %</label><Input type="number" value={form.passMarkPercent} onChange={e => setForm(f => ({ ...f, passMarkPercent: parseInt(e.target.value) || 80 }))} /></div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v as 'Draft' | 'Published' }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Draft">Draft</SelectItem><SelectItem value="Published">Published</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Content Files</label>
              <input ref={contentRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={uploadContent} />
              <Button type="button" variant="outline" size="sm" onClick={() => contentRef.current?.click()} disabled={uploadingContent} className="mb-2">
                {uploadingContent ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading...</> : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload file</>}
              </Button>
              {contentFiles.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-xs text-muted-foreground border border-border px-3 py-2 mb-1 rounded">
                  <span className="truncate flex-1">File {i + 1}</span>
                  <button onClick={() => setContentFiles(fs => fs.filter((_, j) => j !== i))} className="text-destructive ml-2">✕</button>
                </div>
              ))}
            </div>
            <Button onClick={saveModuleForm} disabled={saving} className="w-full bg-primary text-primary-foreground">
              {saving ? 'Saving...' : editMod ? 'Update module' : 'Create module'}
            </Button>
          </div>
          {editMod && (
            <div className="border-t border-border pt-6">
              <h3 className="font-semibold text-foreground mb-4">Quiz Questions</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                <Button size="sm" onClick={generateAI} disabled={generating || !form.contentBody.trim()} className="bg-primary text-primary-foreground">
                  {generating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate with AI</>}
                </Button>
              </div>
              {aiQuestions.length > 0 && !generating && (
                <div className="border border-primary/30 bg-primary/5 rounded p-4 mb-4 space-y-3">
                  <p className="text-xs text-primary font-semibold">{aiQuestions.length} AI Generated</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveAIQuestions} disabled={savingAI} className="bg-primary text-primary-foreground">
                      {savingAI ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving...</> : `Save all ${aiQuestions.length}`}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setAiQuestions([])}>Dismiss</Button>
                  </div>
                </div>
              )}
              {loadingQ ? <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div> : (
                <QuestionManager moduleId={editMod.id} questions={questions} onRefresh={refreshQ} />
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
